// Persistence and retrieval for per-user game statistics.
//
// Only email-authenticated game users are recorded. Players who signed in with
// a game code get a synthetic `player_*` id with no database row, and admins
// sign out of Supabase immediately after their password is verified, so
// neither has an `auth.uid()` to record against.

import { supabase } from "@/supabase";

/**
 * Resolve the id to record statistics against, or null when the current
 * player should not be recorded.
 */
export async function getStatsUserId() {
  try {
    const raw = localStorage.getItem("gameSession");
    if (!raw) return null;

    const session = JSON.parse(raw);
    if (session?.authType !== "email") return null;

    // The Supabase session is authoritative; localStorage alone is not enough
    // because RLS checks auth.uid() on insert.
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id || null;
  } catch (error) {
    console.warn("Could not resolve stats user:", error);
    return null;
  }
}

/**
 * Write one finished game and its deals.
 * Errors are logged and swallowed so a stats failure never interrupts play.
 *
 * @returns {Promise<string|null>} the new session id, or null if nothing was saved
 */
export async function saveGameSession({ summary, deals, departmentId, isDemo, startedAt }) {
  const userId = await getStatsUserId();
  if (!userId) return null;

  if (!summary || summary.dealsCount === 0) return null;

  try {
    const { data: session, error: sessionError } = await supabase
      .from("game_sessions")
      .insert({
        user_id: userId,
        department_id: departmentId ?? null,
        started_at: new Date(startedAt || Date.now()).toISOString(),
        ended_at: new Date().toISOString(),
        total_play_ms: summary.totalPlayMs || 0,
        deals_count: summary.dealsCount,
        cards_dealt: summary.cardsDealt || 0,
        cards_total: summary.cardsTotal,
        cards_correct: summary.cardsCorrect,
        mistakes: summary.mistakes,
        longest_streak: summary.longestStreak,
        avg_deal_ms: summary.avgDealMs,
        is_demo: !!isDemo,
      })
      .select("id")
      .single();

    if (sessionError) throw sessionError;

    const rows = deals
      .filter((d) => d && d.total > 0)
      .map((d) => ({
        session_id: session.id,
        round_number: d.roundNumber,
        dealt_at: d.dealtAt ? new Date(d.dealtAt).toISOString() : null,
        completed_at: d.completedAt ? new Date(d.completedAt).toISOString() : null,
        duration_ms: d.durationMs || 0,
        tier_sequence: d.tiers,
        cards_total: d.total,
        cards_correct: d.correct,
        longest_streak: d.longestStreak,
      }));

    if (rows.length) {
      const { error: roundsError } = await supabase.from("game_rounds").insert(rows);
      if (roundsError) throw roundsError;
    }

    return session.id;
  } catch (error) {
    console.error("Could not save game statistics:", error);
    return null;
  }
}

/** Sessions for the signed-in player, oldest first so charts read left to right. */
export async function fetchMySessions() {
  const userId = await getStatsUserId();
  if (!userId) return { userId: null, sessions: [] };

  const { data, error } = await supabase
    .from("game_sessions")
    .select("*")
    .eq("user_id", userId)
    .order("started_at", { ascending: true });

  if (error) {
    console.error("Could not load game sessions:", error);
    return { userId, sessions: [] };
  }

  return { userId, sessions: data || [] };
}

/** Deals for the signed-in player, used to chart accuracy by deal number. */
export async function fetchMyRounds() {
  const userId = await getStatsUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from("game_rounds")
    .select("*, game_sessions!inner(user_id)")
    .eq("game_sessions.user_id", userId)
    .order("round_number", { ascending: true });

  if (error) {
    console.error("Could not load game rounds:", error);
    return [];
  }

  return data || [];
}

// ---- Admin reads ----
// These call SECURITY DEFINER functions because the admin UI runs without a
// Supabase session, following the existing list_game_users pattern.

export async function fetchAllUserStats() {
  const { data, error } = await supabase.rpc("list_user_game_stats");

  if (error) {
    console.error("Could not load user statistics:", error);
    return [];
  }

  return data || [];
}

export async function fetchSessionsForUser(userId) {
  if (!userId) return [];

  const { data, error } = await supabase.rpc("get_user_game_sessions", {
    p_user_id: userId,
  });

  if (error) {
    console.error("Could not load sessions for user:", error);
    return [];
  }

  return data || [];
}

export async function fetchRoundsForUser(userId) {
  if (!userId) return [];

  const { data, error } = await supabase.rpc("get_user_game_rounds", {
    p_user_id: userId,
  });

  if (error) {
    console.error("Could not load rounds for user:", error);
    return [];
  }

  return data || [];
}
